import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchUserProfile, disconnectYouTube, disconnectFacebook } from "../services/influancerService";
import LoadingSpinner from "../../components/LoadingSpinner";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { INFLUENCER_BASE } from "../constants";

const ConnectSocialMedia = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [social, setSocial] = useState(null);
  const [error, setError] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [disconnectingFacebook, setDisconnectingFacebook] = useState(false);
  const [reconnectingFacebook, setReconnectingFacebook] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "warning",
    onConfirm: null,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await fetchUserProfile();
        if (res.data && res.data.success) {
          setSocial(res.data.social);
          setUserProfile(res.data.user);
        } else {
          setError("Failed to load social info: " + JSON.stringify(res.data));
        }
      } catch (err) {
        setError("Failed to load social info: " + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    setReconnecting(false);
    setReconnectingFacebook(false);
    setDisconnectingFacebook(false);
    setDisconnecting(false);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setReconnecting(false);
        setReconnectingFacebook(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("youtube_expired") === "true") {
      setError("Your YouTube session has expired. Please reconnect your YouTube account to continue using YouTube features.");
    }
  }, [location]);

  const hasEmail = userProfile?.email && userProfile.email.trim() !== "";

  const showConfirmDialog = (config) => {
    setDialogConfig(config);
    setShowDialog(true);
  };

  const hideDialog = () => {
    setShowDialog(false);
    setDialogConfig({ title: "", message: "", confirmText: "Confirm", cancelText: "Cancel", type: "warning", onConfirm: null });
  };

  const handleDialogConfirm = () => {
    if (dialogConfig.onConfirm) dialogConfig.onConfirm();
    hideDialog();
  };

  const handleConnectYouTube = () => {
    if (!hasEmail) { navigate(`${INFLUENCER_BASE}/dashboard/influencer-profile`); return; }
    setReconnecting(true);
    const token = localStorage.getItem("token");
    window.location.href = `https://infuber-auth.onrender.com/auth/google?token=${token}`;
  };

  const handleDisconnectYouTube = () => {
    showConfirmDialog({
      title: "Disconnect YouTube Account",
      message: "Are you sure you want to disconnect your YouTube account? You will lose access to YouTube features and analytics.",
      confirmText: "Yes, Disconnect",
      cancelText: "Cancel",
      type: "danger",
      onConfirm: async () => {
        setDisconnecting(true);
        try {
          const response = await disconnectYouTube();
          if (response.data.success) {
            const res = await fetchUserProfile();
            if (res.data && res.data.success) { setSocial(res.data.social); setUserProfile(res.data.user); }
          } else { setError("Failed to disconnect YouTube: " + response.data.message); }
        } catch (err) { setError("Failed to disconnect YouTube: " + (err.response?.data?.message || err.message)); }
        finally { setDisconnecting(false); }
      },
    });
  };

  const handleConnectFacebook = () => {
    if (!hasEmail) { navigate(`${INFLUENCER_BASE}/dashboard/influencer-profile`); return; }
    setReconnectingFacebook(true);
    const token = localStorage.getItem("token");
    window.location.href = `https://infuber-auth.onrender.com/auth/facebook?token=${token}`;
  };

  const handleDisconnectFacebook = () => {
    showConfirmDialog({
      title: "Disconnect Facebook Account",
      message: "Are you sure you want to disconnect your Facebook account? You will lose access to Facebook features and analytics.",
      confirmText: "Yes, Disconnect",
      cancelText: "Cancel",
      type: "danger",
      onConfirm: async () => {
        setDisconnectingFacebook(true);
        try {
          const response = await disconnectFacebook();
          if (response.data.success) {
            const res = await fetchUserProfile();
            if (res.data && res.data.success) { setSocial(res.data.social); setUserProfile(res.data.user); }
          } else { setError("Failed to disconnect Facebook: " + response.data.message); }
        } catch (err) { setError("Failed to disconnect Facebook: " + (err.response?.data?.message || err.message)); }
        finally { setDisconnectingFacebook(false); }
      },
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Loading your social media..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center p-4 lg:p-6">
        <p className="max-w-xl text-center text-sm font-semibold text-red-600 sm:text-base">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="w-full p-4 lg:p-6">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 md:flex-row md:items-center md:justify-between lg:mb-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">Connect Your Social Media</h1>
          <p className="mt-1 text-sm text-gray-500">Link your social media accounts to unlock advanced features, analytics, and more.</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
        >
          ← Back
        </button>
      </div>

      {/* Social Media Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
        {/* Instagram */}
        <SocialCard
          icon={
            <svg className="h-8 w-8 shrink-0 sm:h-10 sm:w-10" viewBox="0 0 448 512">
              <defs>
                <linearGradient id="ig-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f58529" />
                  <stop offset="30%" stopColor="#dd2a7b" />
                  <stop offset="60%" stopColor="#8134af" />
                  <stop offset="100%" stopColor="#515bd4" />
                </linearGradient>
              </defs>
              <path fill="url(#ig-gradient)" d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9 114.9-51.3 114.9-114.9S287.7 141 224.1 141zm0 186c-39.5 0-71.5-32-71.5-71.5s32-71.5 71.5-71.5 71.5 32 71.5 71.5-32 71.5-71.5 71.5zm146.4-194.3c0 14.9-12 26.9-26.9 26.9s-26.9-12-26.9-26.9 12-26.9 26.9-26.9 26.9 12 26.9 26.9zm76.1 27.2c-1.7-35.3-9.9-66.7-36.2-92.9C385.6 9.9 354.2 1.7 318.9 0 278.4-1.7 169.6-1.7 129.1 0 93.8 1.7 62.4 9.9 36.2 36.2 9.9 62.4 1.7 93.8 0 129.1c-1.7 40.5-1.7 149.3 0 189.8 1.7 35.3 9.9 66.7 36.2 92.9 26.2 26.2 57.6 34.4 92.9 36.2 40.5 1.7 149.3 1.7 189.8 0 35.3-1.7 66.7-9.9 92.9-36.2 26.2-26.2 34.4-57.6 36.2-92.9 1.7-40.5 1.7-149.3 0-189.8zM398.8 388c-7.8 19.6-22.9 34.7-42.5 42.5-29.4 11.7-99.2 9-132.3 9s-102.9 2.6-132.3-9c-19.6-7.8-34.7-22.9-42.5-42.5-11.7-29.4-9-99.2-9-132.3s-2.6-102.9 9-132.3c7.8-19.6 22.9-34.7 42.5-42.5C121.1 9 190.9 11.6 224 11.6s102.9-2.6 132.3 9c19.6 7.8 34.7 22.9 42.5 42.5 11.7 29.4 9 99.2 9 132.3s2.6 102.9-9 132.3z" />
            </svg>
          }
          name="Instagram"
          connected={social?.instagram?.connected}
        >
          {social?.instagram?.connected ? (
            <div className="space-y-1.5 text-sm text-gray-700">
              <InfoRow label="Username" value={social.instagram.username} />
              <InfoRow label="Instagram ID" value={social.instagram.instagramId} />
              <InfoRow label="Account Type" value={social.instagram.accountType} />
              {social.instagram.profileImage?.url && (
                <img src={social.instagram.profileImage.url} alt="Instagram" className="mt-2 h-12 w-12 rounded-full border border-gray-200 object-cover" />
              )}
            </div>
          ) : (
            <div className="flex flex-1 flex-col justify-between">
              <p className="text-sm text-gray-500">Connect your Instagram account to unlock more features.</p>
              <div className="mt-3 flex justify-end">
                <button className="rounded-lg bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 sm:text-sm">
                  Link Account
                </button>
              </div>
            </div>
          )}
        </SocialCard>

        {/* YouTube */}
        <SocialCard
          icon={
            <svg className="h-8 w-8 shrink-0 sm:h-10 sm:w-10" viewBox="0 0 576 512">
              <path fill="#FF0000" d="M549.655 124.083c-6.281-23.65-24.862-42.231-48.511-48.511C458.281 64 288 64 288 64s-170.281 0-213.144 11.572c-23.65 6.281-42.231 24.862-48.511 48.511C16.001 166.946 16.001 256 16.001 256s0 89.054 10.344 131.917c6.281 23.65 24.862 42.231 48.511 48.511C117.719 448 288 448 288 448s170.281 0 213.144-11.572c23.65-6.281 42.231-24.862 48.511-48.511C559.999 345.054 559.999 256 559.999 256s0-89.054-10.344-131.917zM232 336V176l142.857 80L232 336z" />
            </svg>
          }
          name="YouTube"
          connected={social?.youtube?.connected}
        >
          {social?.youtube?.connected ? (
            <div className="flex flex-1 flex-col">
              <div className="space-y-1.5 text-sm text-gray-700">
                <InfoRow label="Channel" value={social.youtube.title} />
                <InfoRow label="Channel ID" value={social.youtube.channelId} />
                <div className="flex items-start gap-3 pt-1">
                  {social.youtube.thumbnail && (
                    <img src={social.youtube.thumbnail} alt="YouTube" className="h-12 w-12 rounded-full border border-gray-200 object-cover" />
                  )}
                  {social.youtube.statistics && (
                    <div className="space-y-0.5 text-xs text-gray-600">
                      <p><b>Subscribers:</b> {social.youtube.statistics.subscriberCount}</p>
                      <p><b>Views:</b> {social.youtube.statistics.viewCount}</p>
                      <p><b>Videos:</b> {social.youtube.statistics.videoCount}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={handleConnectYouTube} disabled={reconnecting} className="flex-1 rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 disabled:opacity-60 sm:text-sm">
                  {reconnecting ? "Reconnecting..." : "Reconnect"}
                </button>
                <button onClick={handleDisconnectYouTube} disabled={disconnecting} className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-600 disabled:opacity-60 sm:text-sm">
                  {disconnecting ? "Disconnecting..." : "Disconnect"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col justify-between">
              {!hasEmail ? (
                <div>
                  <p className="text-xs font-semibold text-red-600 sm:text-sm">⚠️ You need to complete your profile first</p>
                  <p className="mt-1 text-xs text-gray-500 sm:text-sm">Please add your email address before connecting YouTube.</p>
                  <button onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/influencer-profile`)} className="mt-2 rounded-lg bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 sm:text-sm">
                    Complete Profile
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500">Connect your YouTube account to unlock more features.</p>
                  <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-2.5 text-xs text-gray-600">
                    <b>Note:</b> You can only add videos from your own YouTube channel.
                  </div>
                </div>
              )}
              <div className="mt-3 flex justify-end">
                <button
                  onClick={handleConnectYouTube}
                  disabled={!hasEmail || reconnecting}
                  className={`rounded-lg px-4 py-2 text-xs font-semibold transition sm:text-sm ${hasEmail ? "bg-orange-500 text-white hover:bg-orange-600" : "cursor-not-allowed bg-gray-200 text-gray-400"}`}
                >
                  {reconnecting ? "Connecting..." : hasEmail ? "Link Account" : "Complete Profile First"}
                </button>
              </div>
            </div>
          )}
        </SocialCard>

        {/* Facebook */}
        <SocialCard
          icon={
            <svg className="h-8 w-8 shrink-0 sm:h-10 sm:w-10" viewBox="0 0 320 512">
              <path fill="#1877F2" d="M279.14 288l14.22-92.66h-88.91V127.36c0-25.35 12.42-50.06 52.24-50.06H293V6.26S273.54 0 256.36 0C183.74 0 140.05 44.38 140.05 124.72v70.62H56v92.66h84.05V512h100.2V288z" />
            </svg>
          }
          name="Facebook"
          connected={social?.facebook?.connected}
        >
          {social?.facebook?.connected ? (
            <div className="flex flex-1 flex-col">
              <div className="space-y-1.5 text-sm text-gray-700">
                <InfoRow label="Name" value={social.facebook.name} />
                <InfoRow label="Email" value={social.facebook.email} />
                <InfoRow label="Facebook ID" value={social.facebook.id} />
                {social.facebook.picture && (
                  <img src={social.facebook.picture} alt="Facebook" className="mt-1 h-12 w-12 rounded-full border border-gray-200 object-cover" />
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={handleConnectFacebook} disabled={reconnectingFacebook} className="flex-1 rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 disabled:opacity-60 sm:text-sm">
                  {reconnectingFacebook ? "Reconnecting..." : "Reconnect"}
                </button>
                <button onClick={handleDisconnectFacebook} disabled={disconnectingFacebook} className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-600 disabled:opacity-60 sm:text-sm">
                  {disconnectingFacebook ? "Disconnecting..." : "Disconnect"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col justify-between">
              {!hasEmail ? (
                <div>
                  <p className="text-xs font-semibold text-red-600 sm:text-sm">⚠️ You need to complete your profile first</p>
                  <p className="mt-1 text-xs text-gray-500 sm:text-sm">Please add your email address before connecting Facebook.</p>
                  <button onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/influencer-profile`)} className="mt-2 rounded-lg bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 sm:text-sm">
                    Complete Profile
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Connect your Facebook account to unlock advanced analytics, posting, and insights.</p>
              )}
              <div className="mt-3 flex justify-end">
                <button
                  onClick={handleConnectFacebook}
                  disabled={!hasEmail || reconnectingFacebook}
                  className={`rounded-lg px-4 py-2 text-xs font-semibold transition sm:text-sm ${hasEmail ? "bg-orange-500 text-white hover:bg-orange-600" : "cursor-not-allowed bg-gray-200 text-gray-400"}`}
                >
                  {reconnectingFacebook ? "Connecting..." : hasEmail ? "Link Account" : "Complete Profile First"}
                </button>
              </div>
            </div>
          )}
        </SocialCard>
      </div>

      {/* Notes */}
      <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mt-6">
        <h3 className="mb-3 text-sm font-bold text-gray-900 sm:text-base">Notes</h3>
        <ul className="space-y-2 text-xs text-gray-600 sm:text-sm">
          <li className="flex items-start gap-2">
            <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
            You can only add videos from your own <b>YouTube channel</b>. Videos from other channels will be rejected.
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
            We respect your <b>privacy</b> and we don't share or store any personal <b>information</b>.
          </li>
        </ul>
      </div>

      <ConfirmationDialog
        isOpen={showDialog}
        onClose={hideDialog}
        onConfirm={handleDialogConfirm}
        title={dialogConfig.title}
        message={dialogConfig.message}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
        type={dialogConfig.type}
      />
    </div>
  );
};

/* --------------------------------- Helpers -------------------------------- */

const SocialCard = ({ icon, name, connected, children }) => (
  <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md sm:p-5">
    <div className="mb-3 flex items-center gap-3">
      {icon}
      <div>
        <p className="text-sm font-bold text-gray-900 sm:text-base">{name}</p>
        <p className={`text-xs font-semibold ${connected ? "text-green-600" : "text-red-500"}`}>
          {connected ? "Connected" : "Not Connected"}
        </p>
      </div>
    </div>
    <div className="flex flex-1 flex-col">{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-baseline gap-1.5">
    <span className="text-xs font-semibold text-gray-500">{label}:</span>
    <span className="truncate text-sm text-gray-800">{value}</span>
  </div>
);

export default ConnectSocialMedia;
